import Modal from "Common/Components/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";
import React, { useState, useEffect } from "react";
import { FileEdit, Plus } from "lucide-react";
import Select from "react-select";
import { useAppServices } from "hook/services";

interface Country {
  iso2: string;
  iso3: string;
  country: string;
  cities: string[];
}

interface Option {
  value: string;
  label: string;
}
interface propTypes {
  handleRefresh: any;
  toggle: any;
  show: boolean;
  editData: any;
}

const DeviceEditModal = ({
  handleRefresh,
  show,
  toggle,
  editData,
}: propTypes) => {
  const AppService = useAppServices();
  const handleSubmit = async (values: any) => {
    const payload = {
      ...values,
      _id: editData._id,
    };
    const { response, error } = await AppService.sub_account.update({
      payload,
      toaster: true,
    });
    if (response) {
      toggle();
      handleRefresh();
    }
  };

  // Formik + Yup
  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editData?.name || "",
      email: editData?.email || "",
      phone: editData?.phone || "",
      website: editData?.website || "",
      address: editData?.address || "",
      country: editData?.country || "",
      city: editData?.city || "",
      state: editData?.state || "",
      zip: editData?.zip || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter name"),
      email: Yup.string().email("Invalid email").required("Please enter email"),
      phone: Yup.string().required("Please enter phone"),
      website: Yup.string().url("Invalid website URL").nullable(),
      address: Yup.string().required("Please enter address"),
      country: Yup.string().required("Please select country"),
      city: Yup.string().nullable(),
      state: Yup.string().nullable(),
      zip: Yup.string().required("Please enter ZIP code"),
    }),
    onSubmit: (values: any) => {
      handleSubmit(values);
    },
  });


  return (
    <>
      <Modal
        show={show}
        onHide={toggle}
        id="deviceModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[40rem] bg-white shadow rounded-md dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20">
          <Modal.Title className="text-16 font-semibold">
            Edit Device
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block mb-1 text-sm font-medium">Name</label>
              <input
                type="text"
                name="name"
                placeholder="Enter name"
                className="form-input w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
              />
              {formik.touched.name && formik.errors.name && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.name}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block mb-1 text-sm font-medium">Email</label>
              <input
                type="email"
                name="email"
                placeholder="Enter email"
                className="form-input w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
              />
              {formik.touched.email && formik.errors.email && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.email}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block mb-1 text-sm font-medium">Phone</label>
              <input
                type="text"
                name="phone"
                placeholder="Enter phone number"
                className="form-input w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.phone}
              />
              {formik.touched.phone && formik.errors.phone && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.phone}
                </p>
              )}
            </div>

            {/* Website */}
            <div>
              <label className="block mb-1 text-sm font-medium">Website</label>
              <input
                type="text"
                name="website"
                placeholder="https://example.com"
                className="form-input w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.website}
              />
              {formik.touched.website && formik.errors.website && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.website}
                </p>
              )}
            </div>

            {/* Address */}
            <div>
              <label className="block mb-1 text-sm font-medium">Address</label>
              <input
                type="text"
                name="address"
                placeholder="Enter address"
                className="form-input w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.address}
              />
              {formik.touched.address && formik.errors.address && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.address}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="text-red-500 bg-white border btn hover:text-red-600"
                onClick={() => {
                  formik.resetForm();
                  toggle();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
              >
                Edit Device
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DeviceEditModal;
